/**
 * One captured receipt to one document: create it with the photos and what was typed at the
 * table, then read it. The scan the phone made (lib/scan/photo.ts) is the file from the start and
 * the compressed photos are the originals; services/ai only reads them. A capture without one
 * (a photo the phone could not decode) is scanned there, and its scan replaces the file then.
 */
import type { QueuedCapture, SendOutcome } from "@/lib/capture/capture-queue";
import { isConnectionError } from "@/lib/capture/capture-queue";
import { updateDocument, uploadDocument, type DocumentKind } from "./documents";
import { extractDocument, extractionToPatch, scanToPatch } from "./extract";

export type CaptureContext = {
  userId: string;
  /** The business's own UID and IBAN, never to be read as the other party's. */
  ownVatId?: string;
  ownIban?: string;
  ownNames?: string;
};

/** A capture, or a photo from the upload dialog, which may also be an outgoing invoice. */
export type SendableCapture = Omit<QueuedCapture, "kind"> & { kind: DocumentKind };

export async function sendCapture(
  item: SendableCapture,
  context: CaptureContext,
  /** Called once the document exists, so a retry does not create it again. */
  onCreated: (documentId: string) => Promise<void>,
): Promise<SendOutcome> {
  const [first, ...pages] = item.pages;
  if (!first) throw new Error("A capture needs at least one photo");
  const typed = {
    ...(item.category ? { category: item.category } : {}),
    ...(item.deductiblePercent !== undefined ? { deductiblePercent: item.deductiblePercent } : {}),
    ...(item.notes ? { notes: item.notes } : {}),
    ...(item.tip ? { tip: item.tip } : {}),
  };

  let documentId = item.documentId;
  let docNumber: string | undefined;
  if (!documentId) {
    const created = await uploadDocument({
      workspace: item.workspaceId,
      kind: item.kind,
      ...(item.scan
        ? { file: item.scan.pdf, originals: item.pages, scan: item.scan.pages }
        : { file: first, pages }),
      uploadedBy: context.userId,
      extra: typed,
    });
    documentId = created.id;
    docNumber = created.docNumber;
    await onCreated(documentId);
  }

  try {
    const result = await extractDocument(
      first,
      {
        kind: item.kind,
        ownVatId: context.ownVatId,
        ownIban: context.ownIban,
        ownNames: context.ownNames,
      },
      item.scan
        ? { pages, corners: item.scan.pages.map((page) => page.corners), scannedOnPhone: true }
        : { pages },
    );
    // What was typed at the table wins: a tip entered there is never replaced by a reading.
    const updated = await updateDocument(documentId, {
      ...extractionToPatch(result, typed),
      ...(item.scan ? {} : scanToPatch(result, first.name)),
    });
    // Corners handed in are never "not found" to services/ai; the phone knows which it missed.
    const missed = item.scan?.pages.some((page) => page.corners === null);
    return {
      documentId,
      docNumber: updated.docNumber || docNumber,
      extracted: true,
      grossTotal: updated.grossTotal,
      partnerName: updated.partnerName,
      confidence: result.confidence,
      warnings: missed ? [...result.warnings, "scan_not_found"] : result.warnings,
    };
  } catch (error) {
    if (isConnectionError(error)) throw error;
    // Saved but not read: it waits in the documents for a person or a re-read.
    await updateDocument(documentId, { ocrStatus: "failed", status: "review" });
    return {
      documentId,
      docNumber,
      extracted: false,
      warnings: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
